function roundUp(value) {
  const highest = Math.max(1, value);
  const magnitude = 10 ** Math.floor(Math.log10(highest));
  return Math.ceil(highest / magnitude) * magnitude;
}

function getTickStep(range) {
  const roughStep = Math.max(range / 5, 1);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  return (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
}

function getAlignedTickStep(range) {
  const roughStep = Math.max(range / 6, 5);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  return Math.max(5, (normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10) * magnitude);
}

function getCeilingTickStep(value) {
  const roughStep = Math.max(value, 5);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const multiplier = [1, 2, 2.5, 5, 10].find((step) => step * magnitude >= roughStep);
  return multiplier * magnitude;
}

export function getAlignedYAxisTickAmount(maxValues) {
  const values = maxValues.filter((value) => Number.isFinite(value) && value > 0);
  let bestIntervals = 6;
  let bestScore = Infinity;

  for (let intervals = 2; intervals <= 8; intervals += 1) {
    const score = values.reduce((sum, value) => {
      const max = getCeilingTickStep(value / intervals) * intervals;
      return sum + (max - value) / Math.max(value, 5);
    }, 0) + Math.abs(intervals - 6) * 0.02;
    if (score < bestScore) {
      bestScore = score;
      bestIntervals = intervals;
    }
  }

  return bestIntervals + 1;
}

function createTickPositioner(originalMin, rounding, tickAmount) {
  return function dynamicTickPositioner() {
    const dataMin = Number.isFinite(this.dataMin) ? this.dataMin : 0;
    const dataMax = Number.isFinite(this.dataMax) ? this.dataMax : 0;
    let max = dataMax > 0
      ? rounding === 'nice' || rounding === 'aligned' ? dataMax : roundUp(dataMax)
      : dataMin < 0 ? 0 : 1;
    const lowerBound = Number.isFinite(originalMin) ? Math.min(originalMin, dataMin) : Math.min(0, dataMin);
    if (rounding === 'aligned') {
      const intervals = Math.max(2, (tickAmount || 7) - 1);
      const min = Math.min(0, lowerBound);
      const step = getCeilingTickStep((max - min) / intervals);
      return Array.from({ length: intervals + 1 }, (_, index) => min + index * step);
    }
    const step = rounding === 'nice' ? getAlignedTickStep(max - lowerBound) : getTickStep(max - lowerBound);
    if (rounding === 'nice') {
      max = Math.ceil(max / step) * step;
    }
    const min = Math.floor(lowerBound / step) * step;
    const ticks = [];

    for (let tick = min; tick < max; tick += step) {
      ticks.push(tick);
    }

    ticks.push(max);
    return ticks;
  };
}

function withDynamicAxis(axisOptions = {}, rounding) {
  return {
    ...axisOptions,
    min: undefined,
    max: undefined,
    softMin: undefined,
    softMax: undefined,
    floor: undefined,
    ceiling: undefined,
    minRange: undefined,
    tickAmount: undefined,
    tickInterval: undefined,
    tickPositions: undefined,
    tickPositioner: createTickPositioner(axisOptions.min, rounding, axisOptions.tickAmount),
    minPadding: 0,
    maxPadding: 0,
    startOnTick: true,
    endOnTick: true,
    alignTicks: false,
  };
}

export function withDynamicYAxis(options, rounding = 'magnitude') {
  if (!options || typeof options !== 'object') {
    return options;
  }

  // Highcharts computes these extremes from visible series, including stack totals.
  return {
    ...options,
    chart: { ...options.chart, ignoreHiddenSeries: true },
    yAxis: Array.isArray(options.yAxis)
      ? options.yAxis.map((axis) => withDynamicAxis(axis, rounding))
      : withDynamicAxis(options.yAxis, rounding),
  };
}
